import { DocumentNode } from '@apollo/client';
import { MenuItemProps, SxProps } from '@mui/material';
import { FC } from 'react';

export type BaseProps = {
  /**
   * To use for testing
   */
  dataTestId?: string;
};

type GeneratedFragmentTypes<T> = {
  [K in keyof T]: T[K];
};

/**
 * Merges TProps with TFragments, allowing TFragments to win when TProps is empty.
 * Handles both `Record<string, never>` and truly empty object types.
 */
type MergeProps<TProps, TFragments> = [TProps] extends [Record<string, never>]
  ? TFragments
  : keyof TProps extends never
    ? TFragments
    : TProps & TFragments;

export interface FCWithFragments<TFragments extends GeneratedFragmentTypes<TFragments>, TProps = object> extends FC<
  MergeProps<TProps, TFragments>
> {
  fragments: Record<keyof TFragments, DocumentNode>;
}

/**
 * This function doesn't really "do anything" at runtime, it's just the identity
 * function. Its only purpose is to defeat TypeScript's type widening.
 * Use this for typed sx maps without relying on a runtime styling helper.
 *
 * @param styles a set of style mappings
 * @returns the same styles that were passed in
 */
export const createSxProps = <ClassKey extends string, Props extends SxProps = object>(
  styles: Record<ClassKey, Props>
): Record<ClassKey, Props> => {
  return styles;
};

export interface FunctionWithFragments<TFragments extends GeneratedFragmentTypes<TFragments>, TArgs, R>
  // eslint-disable-next-line @typescript-eslint/ban-types
  extends Function {
  (fragments: TFragments, args: TArgs): R;
  fragments: Record<keyof TFragments, DocumentNode>;
}

export interface MenuOptionWithIdNumber extends Omit<MenuItemProps, 'id'> {
  id: number;
}
